import {ReactNode, createContext, useCallback, useContext, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router';

import {brandT} from '@/lib/types/brand.types';
import {distributorT} from '@/lib/types/distributor.types';
import {retailerT} from '@/lib/types/retailer.types';

import {defaultCreateData} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useItemQuery} from '@/hooks/useItemQuery';

import {useAuth} from '@/providers/components/authProvider';
import {useBrands} from '@/providers/components/brandsProvider';

/* ******************************************************************
 * BrandDetailsContext
 ****************************************************************** */
type BrandDetailsContextT = {
  // Properties
  brand: brandT | undefined;
  brandId: number;
  isLoading: boolean;
  error: Error | null;
  // Functions
  refreshBrand: () => Promise<void>;
  updateBrand: (brand: brandT) => Promise<brandT>;
  deleteBrand: () => Promise<void>;
  searchDistributorsToAdd: (name: string) => Promise<distributorT[]>;
  addDistributorToBrand: (distributorId: number) => Promise<void>;
  removeDistributorFromBrand: (distributorId: number) => Promise<void>;
  searchRetailersToAdd: (name: string) => Promise<retailerT[]>;
  addRetailerToBrand: (retailerId: number) => Promise<void>;
  removeRetailerFromBrand: (retailerId: number) => Promise<void>;
};

const BrandDetailsContext = createContext({} as BrandDetailsContextT);

/* ******************************************************************
 *
DetailsProvider
 ****************************************************************** */
type BrandDetailsProviderPropsT = {
  children: ReactNode;
};

/**
 * @param {BrandDetailsProviderPropsT} props - The component props.
 * @returns {Context<BrandDetailsContextT>} - The brand provider component.
 */
export const BrandDetailsProvider = ({children}: BrandDetailsProviderPropsT) => {
  const navigate = useNavigate();
  const {isAdministrator} = useAuth();
  const {id: brandIdFromParams} = useParams();
  const {updateData: updateBrandMutation, deleteData: deleteBrandMutation} = useBrands();

  // Constants
  const brandId = Number(brandIdFromParams);

  // TanStack Queries
  const {data: fetchedBrand, isLoading, isFetching, error, refetch} = useItemQuery({table: 'Brand', id: brandId});

  // Functions
  /*
   * Gets a brand records from the database.
   */
  const getBrand = useCallback(async () => {
    if (!isAdministrator) throw new Error('Error getting brands');
    try {
      const {data} = await refetch();
      const _brand = data ?? null;
      return _brand;
    } catch (error) {
      console.error('Error in getBrand:', error);
      return undefined;
    }
  }, [isAdministrator, refetch]);

  /*
   * Refreshes the brand data from the database.
   */
  const refreshBrand = async () => {
    await getBrand();
  };

  /*
   * Updates the brand in the database.
   */
  const updateBrand = async (brand: brandT) => {
    if (!isAdministrator) throw new Error('Error updating brand');

    try {
      const updatedBrand = await updateBrandMutation(brand);
      return updatedBrand;
    } catch (error) {
      console.error('Error in updateRetailer:', error);
      throw error;
    }
  };

  /*
   * Deletes the brand from the database.
   */
  const deleteBrand = async () => {
    if (!isAdministrator) throw new Error('Error deleting brand');

    // We must have a brand to delete
    if (!fetchedBrand) throw new Error('Error deleting brand');

    try {
      await deleteBrandMutation(fetchedBrand.brand_id);
      navigate('/admin/brands');
    } catch (error) {
      console.error('Error in deleteBrand:', error);
      throw error;
    }
  };

  /*
   * Searches for distributors to add to the brand
   */

  const searchDistributorsToAdd = async (name: string) => {
    try {
      const query = SupabaseInstance.supabase.from('Distributor').select('*').ilike('distributor_name', `%${name}%`);

      // Execute the query
      const {data: distributors, error: distributorError} = await query;

      if (distributorError) {
        console.error('Error in searchDistributorsToAdd:', distributorError);
        throw new Error(distributorError.message);
      }

      return distributors;
    } catch (error) {
      console.error('Error in searchDistributorsToAdd:', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Add a distributor to the brand.
   */
  const addDistributorToBrand = async (distributorId: number) => {
    try {
      const createData = await defaultCreateData();

      const data = {
        ...createData,
        fk_brand_id: brandId,
        fk_distributor_id: distributorId,
        date_removed: null,
        removed_by: null,
      };

      const {error} = await SupabaseInstance.supabase.from('Distributor_Brand').upsert(data, {
        onConflict: 'fk_distributor_id,fk_brand_id',
      });

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('error in add distributor', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Removes a distributor from the brand
   */
  const removeDistributorFromBrand = async (distributorId: number) => {
    try {
      const {data: user} = await SupabaseInstance.supabase.auth.getUser();
      const {error} = await SupabaseInstance.supabase
        .from('Distributor_Brand')
        .update({date_removed: new Date().toISOString(), removed_by: user.user?.id})
        .eq('fk_distributor_id', distributorId)
        .eq('fk_brand_id', brandId);

      if (error) throw new Error(error.message);
    } catch (error) {
      console.error('error in remove distributor', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Search for retailers to add to the brand
   */
  const searchRetailersToAdd = async (name: string) => {
    try {
      const query = SupabaseInstance.supabase.from('Retailer').select('*').ilike('retailer_name', `%${name}%`);

      // Execute the query
      const {data: retailers, error: retailerError} = await query;

      if (retailerError) {
        console.error('Error in searchRetailersToAdd:', retailerError);
        throw new Error(retailerError.message);
      }

      return retailers;
    } catch (error) {
      console.error('Error in searchRetailersToAdd:', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Add a retailer to the brand.
   */
  const addRetailerToBrand = async (retailerId: number) => {
    try {
      const createData = await defaultCreateData();

      const data = {
        ...createData,
        fk_brand_id: brandId,
        fk_retailer_id: retailerId,
        date_removed: null,
        removed_by: null,
      };

      const {error} = await SupabaseInstance.supabase.from('Retailer_Brand').upsert(data, {
        onConflict: 'fk_retailer_id,fk_brand_id',
      });

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('error in add retailer', error);
      throw new Error((error as Error).message);
    }
  };

  /*
   * Removes a retailer from the brand
   */
  const removeRetailerFromBrand = async (retailerId: number) => {
    try {
      const {data: user} = await SupabaseInstance.supabase.auth.getUser();
      const {error} = await SupabaseInstance.supabase
        .from('Retailer_Brand')
        .update({date_removed: new Date().toISOString(), removed_by: user.user?.id})
        .eq('fk_retailer_id', retailerId)
        .eq('fk_brand_id', brandId);

      if (error) throw new Error(error.message);
    } catch (error) {
      console.error('error in remove retailer', error);
      throw new Error((error as Error).message);
    }
  };

  /*******************************************************************
   * Configuration
   ********************************************************************/
  useEffect(() => {
    // If there's an error getting the brand, navigate back to the brand list
    if (error || !brandId) {
      console.error(error);
      navigate('/admin/brands');
    }
  }, [error, navigate, brandId, fetchedBrand]);

  return (
    <BrandDetailsContext.Provider
      value={{
        brand: fetchedBrand,
        brandId,
        isLoading: isLoading || isFetching,
        error,
        refreshBrand,
        updateBrand,
        deleteBrand,
        addDistributorToBrand,
        removeDistributorFromBrand,
        searchDistributorsToAdd,
        searchRetailersToAdd,
        addRetailerToBrand,
        removeRetailerFromBrand,
      }}
    >
      {children}
    </BrandDetailsContext.Provider>
  );
};

/* ******************************************************************
 * useBrand Hook
 ****************************************************************** */
export const useBrand = () => {
  const context = useContext(BrandDetailsContext);
  if (context && Object.keys(context).length === 0 && Object.getPrototypeOf(context) === Object.prototype) {
    throw new Error('useBrand must be used within a BrandDetailsProvider');
  }

  return context;
};
